import { useSyncExternalStore } from 'react'
import type { CallMediaType, VoiceCallPeer } from '../types/voice-call'

/**
 * A second incoming 1:1 call that arrived while the user is already in an active
 * call (call-waiting). Held off to one side of the main voice-call session so the
 * user can MERGE the new caller into the current call (1:1 → group) or DECLINE.
 *
 * Only one waiting call is tracked at a time; any further invites that arrive
 * while one is pending are auto-declined (BUSY) by the controller.
 */
export type WaitingCall = {
  callId: string
  callerId: string
  calleeId: string
  conversationId?: string | null
  peer: VoiceCallPeer
  mediaType: CallMediaType
  startedAt: number
  /** Telehealth session call (INVITE payload) — never merged into a group. */
  sessionUuid?: string | null
}

let waiting: WaitingCall | null = null
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

export function getWaitingCall(): WaitingCall | null {
  return waiting
}

export function setWaitingCall(next: WaitingCall | null): void {
  waiting = next
  emit()
}

export function clearWaitingCall(): void {
  if (waiting === null) return
  waiting = null
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useWaitingCall(): WaitingCall | null {
  return useSyncExternalStore(subscribe, getWaitingCall, getWaitingCall)
}
